import React from 'react';
import { CornerRightDown, Plus } from 'lucide-react';
import { isDark } from '../lib/covers';
import { tiltFor } from '../lib/paper';

const countLabel = (n: number): string => `${n} ${n === 1 ? 'page' : 'pages'}`;

/** Inline rename for the label: the title becomes a field in the same type. */
export interface LabelEditor {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

/**
 * The white label stuck on a notebook cover (mobile's NotebookLabel): name in
 * ink over a ruled line, page count below. Paper, not the cover colour, so the
 * name reads on every cover. Pass `editor` to rename in place.
 */
export const NotebookLabel: React.FC<{
  name: string;
  count: number;
  size?: 'sm' | 'lg';
  editor?: LabelEditor;
  onClickName?: () => void;
}> = ({ name, count, size = 'sm', editor, onClickName }) => {
  const lg = size === 'lg';
  const nameClass = `m-0 font-headline font-medium italic text-on-surface ${
    lg ? 'text-[1.75rem] leading-8 md:text-[2.25rem] md:leading-[2.6rem]' : 'line-clamp-2 text-base leading-5'
  }`;
  return (
    <div
      className={`-rotate-[1.5deg] rounded-[5px] bg-surface-container-lowest ${lg ? 'px-4 pb-2.5 pt-3' : 'px-2.5 pb-1.5 pt-2'}`}
    >
      {editor ? (
        <input
          value={editor.value}
          onChange={(e) => editor.onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') editor.onSubmit();
            if (e.key === 'Escape') editor.onCancel();
          }}
          autoFocus
          onFocus={(e) => e.currentTarget.select()}
          maxLength={100}
          aria-label="Notebook name"
          className={`${nameClass} w-full border-none bg-transparent p-0 outline-none`}
        />
      ) : onClickName ? (
        <button
          type="button"
          onClick={onClickName}
          title="Rename notebook"
          className={`${nameClass} block cursor-text border-none bg-transparent p-0 text-left`}
        >
          {name}
        </button>
      ) : (
        <p className={nameClass}>{name}</p>
      )}
      <div className={`mt-1 ${editor ? 'h-0.5 bg-accent' : 'h-px bg-[rgba(28,27,25,0.12)]'}`} />
      <p className="m-0 mt-1 font-body text-[10.5px] font-medium leading-[14px] text-on-surface-variant">
        {countLabel(count)}
      </p>
    </div>
  );
};

interface CollectionCardProps {
  name: string;
  noteCount: number;
  /** Cover colour "#rrggbb" (see coverFor in lib/covers). */
  cover: string;
  id: number;
  onClick: () => void;
  /** A page is being dragged somewhere on the shelf. */
  isDropTarget?: boolean;
  /** …and it's over this notebook. */
  isDropActive?: boolean;
  onDragOver?: (e: React.DragEvent) => void;
  onDragEnter?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
}

/**
 * Library card styled as a notebook (mobile's CollectionCard): bound spine,
 * bookmark ribbon, page edges peeking out and a paper label, each notebook at
 * its own slight angle as if dropped on the shelf. Also a drop target for
 * dragged loose pages.
 */
export const CollectionCard: React.FC<CollectionCardProps> = ({
  name,
  noteCount,
  cover,
  id,
  onClick,
  isDropTarget = false,
  isDropActive = false,
  onDragOver,
  onDragEnter,
  onDragLeave,
  onDrop,
}) => {
  const dark = isDark(cover);
  const spine = dark ? 'rgba(0,0,0,0.22)' : 'rgba(0,0,0,0.13)';
  const seam = dark ? 'rgba(255,255,255,0.16)' : 'rgba(255,255,255,0.55)';
  // Paper ribbon on dark covers, ink on light ones.
  const ribbon = dark ? 'var(--surface)' : 'var(--primary)';

  return (
    <div
      className={`relative transition-transform duration-200 ${isDropActive ? 'scale-105' : ''}`}
      onDragOver={onDragOver}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onClick())}
        aria-label={`${name}, ${countLabel(noteCount)}`}
        className={`pb-squish relative block h-[154px] cursor-pointer pr-[5px] outline-none focus-visible:ring-2 focus-visible:ring-accent md:h-[172px] ${
          isDropTarget ? '[&_*]:pointer-events-none' : ''
        }`}
        style={{ '--tilt': `${tiltFor(id) * 1.2}deg`, boxShadow: 'none' } as React.CSSProperties}
      >
        {/* Page block peeking out past the cover's fore-edge. */}
        <span className="absolute bottom-[5px] left-2.5 right-0 top-[5px] rounded-2xl border border-solid border-outline-variant bg-surface-container-lowest" />
        <span className="absolute bottom-[3px] left-2.5 right-[2.5px] top-[3px] rounded-2xl border border-solid border-outline-variant bg-surface-container-lowest" />

        <div
          className="relative flex h-full flex-col justify-end overflow-hidden rounded-2xl shadow-[0_12px_40px_rgba(28,27,25,0.12)] transition-colors duration-300"
          style={{ backgroundColor: cover }}
        >
          <span className="absolute bottom-0 left-0 top-0 w-[10.5px]" style={{ backgroundColor: spine }} />
          <span className="absolute bottom-0 left-3 top-0 w-px" style={{ backgroundColor: seam }} />
          <span className="absolute right-5 top-0 flex flex-col items-center" aria-hidden>
            <span className="h-[30px] w-3" style={{ backgroundColor: ribbon }} />
            <span
              className="h-0 w-0 border-x-[6px] border-t-[7px] border-solid border-x-transparent"
              style={{ borderTopColor: ribbon }}
            />
          </span>
          <div className="pb-3.5 pl-[21px] pr-2.5">
            <NotebookLabel name={name} count={noteCount} />
          </div>
        </div>
      </div>

      {isDropActive && (
        <>
          <span className="pointer-events-none absolute -bottom-[3px] -left-[5px] -right-px -top-[5px] rounded-[1.3rem] border-2 border-dashed border-accent bg-[rgba(91,63,208,0.06)]" />
          <span className="pb-rise pointer-events-none absolute left-1/2 top-[-12px] flex -translate-x-1/2 items-center gap-1 whitespace-nowrap rounded-full bg-accent px-2.5 py-1 font-body text-[11px] font-semibold leading-[15px] text-on-primary">
            <CornerRightDown size={12} /> Drop to file
          </span>
        </>
      )}
    </div>
  );
};

/** Dashed "blank notebook" at the end of the shelf — the obvious way to add one. */
export const NewNotebookTile: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="mr-[5px] flex h-[151px] w-[calc(100%-5px)] cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-[1.5px] border-dashed border-[rgba(91,63,208,0.45)] bg-[rgba(255,255,255,0.6)] transition-transform duration-150 hover:bg-surface-container-lowest active:scale-[0.97] md:h-[169px]"
  >
    <span className="flex h-[38px] w-[38px] items-center justify-center rounded-full bg-surface-highlight text-accent">
      <Plus size={20} />
    </span>
    <span className="font-body text-[0.8125rem] font-semibold text-accent">New notebook</span>
  </button>
);
